import type { Revision, RevisionPage } from '../../types';
import { mutations } from '../../utils';
import { getPageChildrenInRevision } from './getPageChildrenInRevision';
import { getPageParentInRevision } from './getPageParentInRevision';
import { getRootPagesInRevision } from './getRootPagesInRevision';

interface GetPageNextSiblingOptions<PageType> {
    filterPage?: (page: PageType) => boolean;
}

/**
 * Get the next sibling after a page.
 */
export function getPageNextSiblingInRevision(
    revision: Revision,
    pageId: string,
    options?: GetPageNextSiblingOptions<RevisionPage>
): RevisionPage | null;
export function getPageNextSiblingInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    options?: GetPageNextSiblingOptions<mutations.MutableData<RevisionPage>>
): mutations.MutableData<RevisionPage> | null;
export function getPageNextSiblingInRevision(
    revision: mutations.MutableData<Revision>,
    pageId: string,
    // The implementation signature uses an `any` page type so both public overloads (whose
    // `filterPage` params are contravariantly incompatible) are assignable to it. Not visible to
    // callers.
    options: GetPageNextSiblingOptions<any> = {}
): mutations.MutableData<RevisionPage> | null {
    const { filterPage } = options;

    const revisionId = mutations.get(revision, 'id');
    const page = mutations.getIn(revision, ['pages', pageId] as const);
    if (!page) {
        throw new Error(`Page ${pageId} not found in revision ${revisionId}`);
    }

    const parent = getPageParentInRevision(revision, pageId);
    let pages = parent
        ? getPageChildrenInRevision(revision, mutations.get(parent, 'id'))
        : getRootPagesInRevision(revision);

    if (filterPage) {
        pages = pages.filter((page) =>
            mutations.get(page, 'id') === pageId ? true : filterPage(page)
        );
    }

    const index = pages.findIndex((page) => mutations.get(page, 'id') === pageId);
    const nextIndex = index + 1;

    return pages[nextIndex] || null;
}
